import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ScienceIcon from '@mui/icons-material/Science';
import {
  getPaidValue,
  getExpectedValue,
  getProjectedFund,
  getBgColumnValues,
  getRefundPendingValue,
} from '../../../../components/Table/projectDashboard/formulas';
import { REPORT_OPTIONS } from '../../../../components/Table/projectDashboard/constants';
import {
  WORK_OPTIONS_EXPANDED,
  FINANCE_OPTIONS,
  BG_USAGE_OPTIONS,
  NO_BG_USAGE_KEY,
  LEGAL_OPTIONS,
  PCT_COL_W,
  PCT_DEFAULT_CH,
  SHARED_VALUE_WIDTH_PX,
} from '../constants';
import {
  computeStatusCascade,
  computeFinanceSync,
  computeWorkAutoBump,
  computeWorkAutoRevert,
  fmt,
  fmtPct,
  fmtDate,
  toDateInput,
  getExpandedWidthFromDefault,
  getLongestText,
} from '../helpers';
import { ColumnWidthContext } from '../contexts';
import { StatusGroup } from './StatusGroup';
import { Row, EditableRow } from './Row';
import { SectionHeader } from './SectionHeader';
import { TruncatedTooltipText } from './TruncatedTooltipText';
import { NoteBox } from './NoteBox';
import { POProgressBars } from './POProgressBars';
import { ProjectSandboxDetailTab } from './ProjectSandboxDetailTab';
import { ProjectDashboardTableSandbox } from '../../../../components/Table/projectDashboard/ProjectDashboardTableSandbox';

// โหมดทดสอบ (sandbox) — เปิดจากปุ่ม 🧪 ใน ProjectPopup.js ของแต่ละโปรเจกต์ เอาไว้ให้ HR ลองปรับค่า
// เล่นดูสูตร/ผลลัพธ์ได้อิสระ โดย "ไม่มีทาง" กระทบข้อมูลจริง เพราะ:
//   1) seed ค่าเริ่มต้นจาก deep-clone ของ project จริงที่โหลดอยู่แล้วในหน้าจอ (ไม่ยิง API เพิ่ม)
//   2) การแก้ไขทุกช่องแก้ state ในเครื่อง (useState) ล้วนๆ — ไม่มี dispatch/axios ไปหา backend เลย
//   3) ปิด dialog หรือกด "รีเซ็ตข้อมูล" แล้วค่าที่ลองแก้ทั้งหมดหายไป กลับไปเป็นภาพสดของโครงการจริงเสมอ
// ตั้งใจ "reuse" สูตรการเงิน (projectDashboard/formulas.js), cascade กฎการเปลี่ยนสถานะ, และชิ้นส่วน UI
// (Row/EditableRow/StatusGroup/POProgressBars/NoteBox) ตัวเดียวกับ ProjectSingleDashboard.js (P111)
// เป๊ะๆ ทั้งหมด — กันปัญหาสูตรสองชุดที่ไม่ตรงกัน (ประวัติของระบบนี้เคยโดนปัญหานี้มาแล้วจริง) จึงไม่มีการ
// port สูตรมาเขียนใหม่แม้แต่บรรทัดเดียว มีแค่ layout/handler ที่ตัดทอนให้เหลือเฉพาะส่วนที่แก้ไขได้จริง
// (ตัดปุ่มเลื่อนก่อนหน้า/ถัดไป, ลิงก์โอกาสทางธุรกิจ, ตรวจสอบรายวัน, Export Excel, สวิตช์โชว์สูตร ออก —
// เป็นฟีเจอร์เฉพาะของหน้าโครงการจริง ไม่เกี่ยวกับการ "ลองเล่นตัวเลข")
export function ProjectSandboxDialog({ open, onClose, sourceProject, uiLang = 'en' }) {
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const [p, setP] = useState(null);
  const [tab, setTab] = useState(0);

  const seedFromSource = () => {
    setP(sourceProject ? JSON.parse(JSON.stringify(sourceProject)) : {});
  };

  useEffect(() => {
    if (open) {
      seedFromSource();
      setTab(0);
    }
  }, [open, sourceProject]);

  const applyPatch = (patch) => {
    setP((prev) => ({ ...prev, ...patch }));
  };

  const handleStatusSelect = (field, value) => {
    const patch = computeStatusCascade(p, field, value);
    if (patch) applyPatch(patch);
  };

  const workStatusCurrentKey =
    p?.work_status === 'in_progress' && p?.work_method
      ? `in_progress:${p.work_method}`
      : p?.work_status;

  const handleWorkStatusSelect = (composite) => {
    let workStatus = composite;
    let workMethod = '';
    if (composite.startsWith('in_progress:')) {
      workStatus = 'in_progress';
      workMethod = composite.slice('in_progress:'.length);
    }
    const patch = computeStatusCascade(p, 'work_status', workStatus);
    if (!patch) return;
    patch.work_method = workMethod;
    applyPatch(patch);
  };

  const handleLegalToggle = (key) => {
    handleStatusSelect(key, !p?.[key]);
  };

  if (!p) return null;

  const po = p?.include_vat ? (p?.cost || 0) - (p?.vat || 0) : p?.cost || 0;
  const seedBilled = p?.seed_billed_waiting || 0;
  const seedReceived = p?.seed_received || 0;
  const billedCombined = (p?.billed_waiting || 0) + seedBilled;
  const receivedCombined = (p?.received || 0) + seedReceived;
  const poRemaining = po - receivedCombined - billedCombined;

  const paidVal = getPaidValue(p || {});
  const expectedVal = getExpectedValue(p || {});
  const projectedFund = getProjectedFund(p || {});
  const payableBgEligible =
    p?.bg_usage_category === NO_BG_USAGE_KEY ? 0 : Math.max(Number(projectedFund) || 0, 0);
  const refundPending = getRefundPendingValue(p || {});
  const realProfit =
    po -
    (paidVal +
      expectedVal +
      (Number(p?.ap_pending_unpaid) || 0) +
      (Number(p?.to_spend) || 0) +
      payableBgEligible);
  const { col14, col15, col17 } = getBgColumnValues(p || {});

  const isLawsuit = Boolean(p?.legal_lawsuit);
  const row22Profit = isLawsuit ? receivedCombined - paidVal : realProfit;

  const sharedValueWidth = `${SHARED_VALUE_WIDTH_PX}px`;
  const sharedPctWidth = getExpandedWidthFromDefault(
    getLongestText([
      fmtPct(billedCombined, po),
      fmtPct(receivedCombined, po),
      fmtPct(poRemaining, po),
      fmtPct(p?.total_budget, po),
      fmtPct(realProfit, po) || '0.00%',
    ]),
    `${PCT_COL_W}px`,
    PCT_DEFAULT_CH,
    20,
    1,
  );

  const deliveryDate = p?.delivery_date;
  const creditDays = p?.client_id?.credit_days;
  const paymentCycleDay = p?.payment_cycle_day;
  let expectedDate = null;
  if (deliveryDate && creditDays != null && creditDays !== '') {
    const base = new Date(deliveryDate);
    base.setDate(base.getDate() + Number(creditDays));
    if (paymentCycleDay != null && paymentCycleDay !== '') {
      const day = Number(paymentCycleDay);
      const sameMonth = new Date(base.getFullYear(), base.getMonth(), day);
      expectedDate = sameMonth >= base ? sameMonth : new Date(base.getFullYear(), base.getMonth() + 1, day);
    } else {
      expectedDate = base;
    }
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = expectedDate
    ? Math.round((expectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '92vh' } }}>
      <DialogContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            bgcolor: '#fef9c3',
            border: '1px solid #eab308',
            borderRadius: 1,
            px: 2,
            py: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <ScienceIcon sx={{ color: '#854d0e' }} fontSize="small" />
          <Typography variant="body2" fontWeight={700} color="#854d0e" sx={{ flex: 1 }}>
            {U(
              'โหมดทดสอบ — ข้อมูลจำลอง (คัดลอกจากโครงการนี้) แก้ไขอะไรก็ได้ ไม่ถูกบันทึกและไม่กระทบข้อมูลจริงเด็ดขาด',
              'Sandbox mode — mock data cloned from this project. Edit freely; nothing is ever saved or affects the real data.',
            )}
          </Typography>
          <Tooltip title={U('รีเซ็ตกลับเป็นข้อมูลจริงล่าสุด', 'Reset to the latest real data')}>
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon fontSize="small" />} onClick={seedFromSource} sx={{ color: '#854d0e', borderColor: '#eab30880' }}>
              {U('รีเซ็ตข้อมูล', 'Reset data')}
            </Button>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 32, mb: 1.5 }}>
          <Tab
            value={0}
            label={U('แดชบอร์ด', 'Dashboard')}
            sx={{ minHeight: 32, py: 0.5, fontSize: '0.8rem' }}
          />
          <Tab
            value={1}
            label={U('รายละเอียด', 'Details')}
            sx={{ minHeight: 32, py: 0.5, fontSize: '0.8rem' }}
          />
          <Tab
            value={2}
            label={U('ตาราง P5', 'P5 table')}
            sx={{ minHeight: 32, py: 0.5, fontSize: '0.8rem' }}
          />
        </Tabs>

        {tab === 1 && (
          <ProjectSandboxDetailTab project={p} applyPatch={applyPatch} uiLang={uiLang} />
        )}

        {tab === 2 && (
          <ProjectDashboardTableSandbox
            rows={[p]}
            uiLang={uiLang}
            onApplyPatch={(rowId, patch) => {
              if (rowId === (p?._id || p?.id)) applyPatch(patch);
            }}
          />
        )}

        {tab === 0 && (
        <ColumnWidthContext.Provider value={{ valueWidth: sharedValueWidth, pctWidth: sharedPctWidth }}>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Box sx={{ width: 200, flexShrink: 0 }}>
              <Box sx={{ bgcolor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 1, px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700} color="#1e3a8a">
                  {p?.project_number || '-'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 280px', minWidth: 260, maxWidth: 420, overflow: 'hidden' }}>
              <TruncatedTooltipText
                text={isLawsuit && p?.customer ? `${p.customer} (${U('ฟ้องร้อง', 'Lawsuit')})` : p?.customer}
                variant="body1"
                fontWeight={700}
                color={isLawsuit ? '#dc2626' : undefined}
              />
              <TruncatedTooltipText text={p?.name} variant="body2" color="#2563eb" />
              <Box sx={{ mt: 0.3, width: 'fit-content' }}>
                <EditableRow
                  label={U('ความคืบหน้า', 'Progress')}
                  value={p?.progress ?? 0}
                  displayValue={`${p?.progress ?? 0}%`}
                  fillPct={p?.progress ?? 0}
                  min={0}
                  max={100}
                  onSave={(v) => applyPatch({ progress: v ?? 0 })}
                />
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 260px', minWidth: 240 }}>
              <POProgressBars
                po={po}
                receivedCombined={receivedCombined}
                billedCombined={billedCombined}
                poRemaining={poRemaining}
                paidVal={paidVal}
                col17={col17}
                totalBudget={p?.total_budget}
                targetProfit={(p?.cost || 0) - (p?.total_budget || 0)}
                realProfit={row22Profit}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            {/* Sidebar สถานะ */}
            <Box sx={{ width: 200, flexShrink: 0, bgcolor: isLawsuit ? '#fef2f2' : 'transparent', borderRadius: 1 }}>
              <StatusGroup
                title={U('ด้านงาน', 'Work')}
                color="#2563eb"
                options={WORK_OPTIONS_EXPANDED}
                currentKey={workStatusCurrentKey}
                onSelect={handleWorkStatusSelect}
                dangerBg={isLawsuit}
              />
              <StatusGroup
                title={U('ด้านการเงิน', 'Finance')}
                color="#d97706"
                options={FINANCE_OPTIONS}
                currentKey={p?.finance_status}
                onSelect={(v) => handleStatusSelect('finance_status', v)}
                dangerBg={isLawsuit}
              />
              <StatusGroup
                title={U('หมวดการใช้ BUDGET', 'Budget mode')}
                color="#374151"
                options={BG_USAGE_OPTIONS}
                currentKey={p?.bg_usage_category}
                onSelect={(v) => handleStatusSelect('bg_usage_category', v)}
                getOptionDisabledReason={(opt) => (
                  opt.key === NO_BG_USAGE_KEY && p?.work_status !== 'done'
                    ? U('งานยังไม่100% กดไม่ได้', 'Work not 100% yet')
                    : ''
                )}
                dangerBg={isLawsuit}
              />
              <Box
                sx={{
                  p: 0.65,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isLawsuit ? '#dc262628' : '#7c3aed28',
                  boxShadow: `inset 3px 0 0 ${isLawsuit ? '#dc2626' : '#7c3aed'}`,
                  bgcolor: isLawsuit ? '#fef2f2' : '#fafbff',
                  mb: 0.6,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontWeight: 700, fontSize: '0.67rem', color: isLawsuit ? '#dc2626' : '#7c3aed', mb: 0.3, pb: 0.15, borderBottom: `1px solid ${isLawsuit ? '#dc2626' : '#7c3aed'}25` }}
                >
                  {U('ด้านนิติกรรม', 'Legal')}
                </Typography>
                <Stack spacing={0}>
                  {LEGAL_OPTIONS.map((opt) => {
                    const checked = Boolean(p?.[opt.key]);
                    return (
                      <Box
                        key={opt.key}
                        onClick={() => {
                          if (opt.key === 'legal_warranty') {
                            const input = window.prompt(
                              U('วันครบกำหนดรับประกัน (YYYY-MM-DD) เว้นว่าง = ไม่มีประกัน', 'Warranty end date (YYYY-MM-DD), blank = no warranty'),
                              toDateInput(p?.warranty_end_date),
                            );
                            if (input !== null) handleStatusSelect('warranty_end_date', input || null);
                            return;
                          }
                          handleLegalToggle(opt.key);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.6,
                          py: 0.1,
                          px: 0.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: checked ? `${opt.color}15` : 'transparent',
                          '&:hover': { bgcolor: `${opt.color}10` },
                        }}
                      >
                        <Box sx={{ width: 12, height: 12, borderRadius: 0.5, flexShrink: 0, border: '2px solid', borderColor: checked ? opt.color : '#cbd5e1', bgcolor: checked ? opt.color : 'transparent' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: checked ? opt.color : 'text.primary', fontWeight: checked ? 700 : 400 }}>
                          {opt.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              <StatusGroup
                title={U('รายงาน/เอกสารส่งงาน', 'Report/Documents')}
                color="#0f172a"
                options={REPORT_OPTIONS}
                currentKey={p?.report_status || ''}
                onSelect={(v) => handleStatusSelect('report_status', v)}
                warnEmpty
                dangerBg={isLawsuit}
              />
            </Box>

            {/* คอลัมน์กลาง: Cashflow + งบประมาณ */}
            <Box sx={{ flex: '1 1 280px', minWidth: 260 }}>
              <SectionHeader>{U('ด้านการCASHFLOW', 'Cashflow')}</SectionHeader>
              <Row label="PO (No VAT)" value={fmt(po)} bold pct="" />
              <EditableRow
                label="Invoiced [ VAT incl ]"
                value={seedBilled}
                displayValue={fmt(billedCombined)}
                min={0}
                bold={billedCombined > 0}
                textColor={billedCombined > 0 ? '#16a34a' : undefined}
                pct={fmtPct(billedCombined, po)}
                onSave={(v) => {
                  const val = v ?? 0;
                  const patch = { seed_billed_waiting: val };
                  const newBilledCombined = (p?.billed_waiting || 0) + val;
                  const financeSync = computeFinanceSync(p, po, newBilledCombined, receivedCombined);
                  if (financeSync) Object.assign(patch, financeSync);
                  if (!patch.work_status) {
                    const workBump = computeWorkAutoBump(p, newBilledCombined, receivedCombined);
                    if (workBump) Object.assign(patch, workBump);
                    else {
                      const workRevert = computeWorkAutoRevert(p, newBilledCombined, receivedCombined);
                      if (workRevert) Object.assign(patch, workRevert);
                    }
                  }
                  applyPatch(patch);
                }}
              />
              <EditableRow
                label={U('รับเงินมาแล้ว', 'Received')}
                value={seedReceived}
                displayValue={fmt(receivedCombined)}
                min={0}
                underline="single"
                pct={fmtPct(receivedCombined, po)}
                onSave={(v) => {
                  const val = v ?? 0;
                  const patch = { seed_received: val };
                  const newReceivedCombined = (p?.received || 0) + val;
                  const financeSync = computeFinanceSync(p, po, billedCombined, newReceivedCombined);
                  if (financeSync) Object.assign(patch, financeSync);
                  if (!patch.work_status) {
                    const workBump = computeWorkAutoBump(p, billedCombined, newReceivedCombined);
                    if (workBump) Object.assign(patch, workBump);
                    else {
                      const workRevert = computeWorkAutoRevert(p, billedCombined, newReceivedCombined);
                      if (workRevert) Object.assign(patch, workRevert);
                    }
                  }
                  applyPatch(patch);
                }}
              />
              <Row label={U('ยอดค้างส่งงานจาก PO', 'PO Remaining')} value={fmt(poRemaining)} pct={fmtPct(poRemaining, po)} />
              <Row label={U('BG ที่ตั้งไว้', 'Budget Set')} value={fmt(p?.total_budget)} pct={fmtPct(p?.total_budget, po)} />
              <Row label={U('BG PAID', 'BG Paid')} value={fmt(paidVal)} textColor="#16a34a" />
              <Row label={U('รายการตั้งเบิกที่รอจ่าย', 'Pending Expenses')} value={fmt(expectedVal)} />
              <Row label={U('AP PENDING ตามงบย่อย', 'AP Pending')} value={fmt(p?.ap_pending_unpaid)} />
              <EditableRow
                label={U('ต้องใช้อีก', 'To Spend')}
                value={p?.to_spend ?? 0}
                displayValue={fmt(p?.to_spend)}
                min={0}
                onSave={(v) => applyPatch({ to_spend: v ?? 0 })}
              />
              <Row label={U('BGเหลือที่มีสิทธิจ่าย', 'Eligible BG')} value={fmt(payableBgEligible)} />
              <Row label={U('รอเงินคืน(เช่นค่ามัดจำ)', 'Refund Pending')} value={fmt(refundPending)} />
              <Row
                label={U('สรุปอนาคตต้องจ่ายอีกรวม', 'Total Payable')}
                value={fmt(col17)}
                bold
                textColor={col17 > 0 ? '#d946ef' : col17 < 0 ? '#16a34a' : undefined}
                underline="double"
              />
            </Box>

            {/* คอลัมน์ขวา: เอกสารการส่งงาน + สรุป */}
            <Box sx={{ flex: '1 1 260px', minWidth: 240, display: 'flex', flexDirection: 'column' }}>
              <SectionHeader>{U('ด้านเอกสารการส่งงาน', 'Delivery Documents')}</SectionHeader>
              <EditableRow
                label={U('ส่งงาน วันที่', 'Delivery Date')}
                type="date"
                value={toDateInput(deliveryDate)}
                displayValue={fmtDate(deliveryDate) || '-'}
                onSave={(v) => applyPatch({ delivery_date: v })}
              />
              <Row label={U('เครดิต (วัน)', 'Credit (Days)')} value={creditDays != null && creditDays !== '' ? creditDays : '-'} />
              <EditableRow
                label={U('รอบจ่ายเงิน', 'Pay Cycle')}
                value={paymentCycleDay}
                displayValue={paymentCycleDay != null && paymentCycleDay !== '' ? paymentCycleDay : '-'}
                min={1}
                max={31}
                onSave={(v) => applyPatch({ payment_cycle_day: v })}
              />
              <Row label={U('รับเงิน วันที่', 'Payment Date')} value={fmtDate(expectedDate) || '-'} />
              <Row
                label={U('รับเงิน อีก (วัน)', 'Days to Payment')}
                value={daysLeft == null ? '-' : daysLeft < 0 ? `เกิน ${-daysLeft}` : daysLeft === 0 ? 'วันนี้' : `+${daysLeft}`}
                red={daysLeft != null && daysLeft < 0}
              />

              <SectionHeader>{U('สรุป', 'Summary')}</SectionHeader>
              <Row label={U('สรุป BG คงเหลือ', 'BG Remaining')} value={fmt(col15)} bold={col15 > 0} />
              {col14 != null ? (
                <Box sx={{ bgcolor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 0.9, px: 0.5 }}>
                  <Row label={U('สรุป BG เกิน', 'BG Overrun')} value={fmt(col14)} red bold />
                </Box>
              ) : (
                <Row label={U('สรุป BG เกิน', 'BG Overrun')} value="-" />
              )}
              <Box
                sx={{
                  mt: 0.6,
                  px: 0.5,
                  borderRadius: 0.9,
                  bgcolor: row22Profit >= 0 ? '#dcfce7' : '#fee2e2',
                  border: '1px solid',
                  borderColor: row22Profit >= 0 ? '#86efac' : '#fca5a5',
                }}
              >
                <Row
                  label={isLawsuit ? U('สรุป กำไรคาดการณ์ (ยื่นฟ้อง)', 'Projected Profit (Lawsuit)') : U('สรุป กำไรคาดการณ์', 'Projected Profit')}
                  value={fmt(row22Profit)}
                  pct={fmtPct(row22Profit, po) || '0.00%'}
                  textColor={row22Profit >= 0 ? '#166534' : '#991b1b'}
                  bold
                />
              </Box>

              <NoteBox value={p?.dashboard_note} authorName={U('ทดสอบ', 'Sandbox')} uiLang={uiLang} onSave={(v) => applyPatch({ dashboard_note: v })} />
            </Box>
          </Box>
        </ColumnWidthContext.Provider>
        )}
      </DialogContent>
    </Dialog>
  );
}
